import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import {
	Box,
	Typography,
	Alert,
	Modal,
	IconButton,
} from "@mui/material";
import RoomIcon from "@mui/icons-material/Room";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import CloseIcon from "@mui/icons-material/Close";
import { MutatingDots } from "react-loader-spinner";
import { getAreaMapDetails, getAreaMaps } from "./store/slices/areaMaps";
import getLogoUrl from "./utils/get-image-url";
import PoweredBy from "./component/PoweredBy";
import WidgetTitle from "./component/WidgetTitle";

const AreaMapsWidget = ({
	language,
	eventID,
	organiserID,
	color,
	displayTitle,
	headerTitle,
	grid,
}) => {
	const dispatch = useDispatch();
	const { areaMaps, loading, error, areaMap, areaMapLoading } = useSelector(
		(state) => state.areaMaps
	);

	const [selected, setSelected] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [loadedDetails, setLoadedDetails] = useState({});

	useEffect(() => {
		dispatch(
			getAreaMaps({
				organiserID: organiserID,
				eventID: eventID,
				language: language,
			})
		);
	}, [eventID, organiserID, language, dispatch]);

	useEffect(() => {
		if (!areaMapLoading && areaMap?.id) {
			setLoadedDetails((prev) => ({ ...prev, [areaMap.id]: areaMap }));
		}
	}, [areaMap, areaMapLoading]);

	const handleCardClick = (item) => {
		setSelected(item);
		setModalOpen(true);
		if (!loadedDetails[item.id]) {
			dispatch(
				getAreaMapDetails({
					organiserID: organiserID,
					eventID: eventID,
					language: language,
					id: item.id,
				})
			);
		}
	};

	const handleClose = () => {
		setModalOpen(false);
	};

	if (loading || error) {
		return (
			<Box className="widget-spinner">
				<MutatingDots
					visible={true}
					height="100"
					width="100"
					color="#4fa94d"
					secondaryColor="#4fa94d"
					radius="12.5"
					ariaLabel="mutating-dots-loading"
					wrapperStyle={{}}
					wrapperClass=""
				/>
			</Box>
		);
	}

	const selectedDetail = selected ? loadedDetails[selected.id] : null;
	const selectedImage = selectedDetail?.image || selected?.image;
	const isModalLoading = modalOpen && areaMapLoading && !selectedDetail;

	return (
		<Box
			className="widget-block"
			style={{
				"--color": color,
			}}
		>
			{displayTitle && (
				<WidgetTitle title={headerTitle}><FormattedMessage id="areaMaps" /></WidgetTitle>
			)}

			{Array.isArray(areaMaps) && areaMaps.length > 0 ? (
				<Box
					className="widget-block--area-maps"
					sx={{
						gridTemplateColumns: {
							xs: "repeat(1, 1fr)",
							sm: "repeat(2, 1fr)",
							md: `repeat(${grid}, 1fr)`,
							lg: `repeat(${grid}, 1fr)`,
						},
					}}
				>
					{areaMaps.map((item) => (
						<Box
							key={item.id}
							className="widget-block--area-maps--item"
							onClick={() => handleCardClick(item)}
						>
							<Box className="widget-block--area-maps--item--head">
								<Typography variant="span">{item?.title}</Typography>
							</Box>
							<Box className="widget-block--area-maps--item--image">
								{item?.image ? (
									<img src={getLogoUrl(item.image)} alt={item?.title} />
								) : (
									<RoomIcon />
								)}
							</Box>
							<Box className="widget-block--area-maps--item--foot">
								<ZoomOutMapIcon />
								<FormattedMessage id="enlarge" />
							</Box>
						</Box>
					))}
				</Box>
			) : (
				<Box className="widget-block--search-not">
					<Alert severity="info">
						<FormattedMessage id="areaMapsNotFound" />
					</Alert>
				</Box>
			)}

			{modalOpen && selected && (
				<Modal
					open={modalOpen}
					aria-labelledby="modal-title"
					onClose={handleClose}
				>
					<Box className="widget-block--area-maps--modal">
						<IconButton
							onClick={handleClose}
							sx={{ position: "absolute", top: 8, right: 8 }}
						>
							<CloseIcon />
						</IconButton>
						<Typography
							variant="h6"
							className="widget-block--area-maps--modal--title"
						>
							{selected?.title}
						</Typography>
						{isModalLoading ? (
							<Box className="widget-block--area-maps--modal--spinner">
								<MutatingDots
									visible={true}
									height="80"
									width="80"
									color="#4fa94d"
									secondaryColor="#4fa94d"
									radius="12.5"
									ariaLabel="mutating-dots-loading"
								/>
							</Box>
						) : selectedImage ? (
							<Box className="widget-block--area-maps--modal--image">
								<img src={getLogoUrl(selectedImage)} alt={selected?.title} />
							</Box>
						) : (
							<Box className="widget-block--area-maps--modal--empty">
								<RoomIcon />
							</Box>
						)}
					</Box>
				</Modal>
			)}

			<PoweredBy />
		</Box>
	);
};

export default AreaMapsWidget;
